import type { Block, ResourceLine, Stage } from "@/calc";
import type { BrainingEstimate } from "./types";

/**
 * Что в смете считается содержанием — тем, что проверял руководитель.
 *
 * Статус «проверено» выводится из даты, отдельного поля нет. Раньше после
 * «Проверено» можно было поменять состав, часы и ставки — статус оставался,
 * бейдж показывал дату проверки, а клиентский файл выходил без пометки
 * «черновик». Руководитель проверил одну смету, клиент получил другую.
 *
 * Правка любого из этих полей снимает проверку. Всё остальное — переписка с
 * ассистентом, отметки выпуска, разбор материалов, снимок для обучения — к
 * содержанию документа не относится и проверку не трогает.
 *
 * Этапы сравниваются не целиком, а по тому, что попадает в документ: состав
 * блоков и порядок, включённость, строки с их объёмом, ценой и исполнителем.
 * Замочек, пометка «правлено руками», комментарий менеджера, планка суммы и
 * рентабельности блока в документ не идут — они про то, как смету делали, а
 * не что в ней написано, — и проверку не снимают.
 */
const CONTENT_FIELDS = [
  "title",
  "client",
  "projectType",
  "division",
  "subdivision",
  "taxSchemeId",
  "deferral",
  "assumptions",
] as const satisfies readonly (keyof BrainingEstimate)[];

/** Поля строки, от которых зависят деньги в документе. */
const LINE_FIELDS = [
  "name",
  "unit",
  "qty",
  "priceSource",
  "unitPrice",
  "exactPrice",
  "priceEstimated",
  "rateOverride",
  "invoiceCost",
  "invoiceTaxRate",
  "invoiceTaxKind",
  "ofkCost",
  "contractor",
  "freelance",
] as const satisfies readonly (keyof ResourceLine)[];

const BLOCK_FIELDS = [
  "id",
  "name",
  "goal",
  "result",
  "enabled",
] as const satisfies readonly (keyof Block)[];

function lineContent(l: ResourceLine): unknown {
  return Object.fromEntries(LINE_FIELDS.map((f) => [f, l[f] ?? null]));
}

function blockContent(b: Block): unknown {
  return {
    ...Object.fromEntries(BLOCK_FIELDS.map((f) => [f, b[f] ?? null])),
    lines: b.lines.map(lineContent),
  };
}

function stagesContent(stages: Stage[]): unknown {
  return stages.map((s) => ({ id: s.id, name: s.name, blocks: s.blocks.map(blockContent) }));
}

export function contentChanged(before: BrainingEstimate, after: BrainingEstimate): boolean {
  if (
    JSON.stringify(stagesContent(before.stages)) !== JSON.stringify(stagesContent(after.stages))
  ) {
    return true;
  }
  return CONTENT_FIELDS.some(
    (field) => JSON.stringify(before[field] ?? null) !== JSON.stringify(after[field] ?? null),
  );
}

/**
 * Отпечаток содержания — того же, что сравнивает `contentChanged`.
 *
 * Нужен согласованию: руководитель решает про ту смету, которую видел, а
 * между «посмотрел» и «нажал» менеджер мог её поправить. Кнопка в Телеграме
 * живёт ещё дольше — сообщение могло прийти утром, а нажали вечером.
 * Отпечаток едет вместе с решением, и сервер сверяет его с тем, что лежит в
 * базе: не совпал — решение про другую смету, и принимать его нельзя.
 *
 * Отметка записи `savedAt` для этого не годится: она меняется и от переписки
 * с ассистентом, и от любой правки, которая в документ не попадает.
 *
 * Хеш не криптографический (cyrb53): защищаемся от случайности, а не от
 * злоумышленника. Четырнадцать знаков — чтобы вместе с идентификатором сметы
 * влезть в 64 байта данных кнопки Телеграма.
 */
export function contentFingerprint(estimate: BrainingEstimate): string {
  const text = JSON.stringify([
    stagesContent(estimate.stages),
    CONTENT_FIELDS.map((field) => estimate[field] ?? null),
  ]);

  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
}

/**
 * Снять проверку, если правка её обесценила.
 *
 * Это мгновенная реакция экрана: тост и пометка «черновик» появляются сразу,
 * не дожидаясь ответа. Источник правды — сервер: `guardReview` при записи
 * делает то же самое по версии из базы, и клиентской копии в этом не верит.
 */
export function withApprovalCheck(
  before: BrainingEstimate,
  after: BrainingEstimate,
): { estimate: BrainingEstimate; dropped: boolean } {
  if (!before.approvedAt || after.approvedAt !== before.approvedAt) {
    return { estimate: after, dropped: false };
  }
  if (!contentChanged(before, after)) return { estimate: after, dropped: false };
  return {
    estimate: {
      ...after,
      approvedAt: undefined,
      approvedBy: undefined,
      reviewRequestedAt: undefined,
    },
    dropped: true,
  };
}
